import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import CuentaCombobox from "@/components/CuentaCombobox";
import { Plus, Trash, Clock, Percent } from "@phosphor-icons/react";
import NumberInput from "@/components/ui/NumberInput";

export const ACREDITACION_DEFAULTS = { plazo_acreditacion_dias: 0, plazo_habiles: false, deducciones: [] };

/** Plazo de acreditación + deducciones (comisiones / impuestos) de una caja. */
export function CajaAcreditacionFields({ form, setForm, cuentas }) {
  const deds = form.deducciones || [];
  const setDed = (i, patch) => setForm({ ...form, deducciones: deds.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  const addDed = () => setForm({ ...form, deducciones: [...deds, { nombre: "", porcentaje: "", cuenta_codigo: "" }] });
  const delDed = (i) => setForm({ ...form, deducciones: deds.filter((_, j) => j !== i) });
  const totalPct = deds.reduce((a, d) => a + (Number(d.porcentaje) || 0), 0);

  return (
    <div className="border border-[#E5E5E5] rounded-sm p-3 space-y-3 bg-neutral-50/60" data-testid="caja-acreditacion">
      <div className="text-[11px] uppercase tracking-widest font-mono text-[#666] flex items-center gap-1.5">
        <Clock size={12} weight="bold" /> Acreditación de cobros
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label className="text-[12px]">Plazo hasta disponer del dinero (días)</Label>
          <NumberInput min={0} className="rounded-sm mt-1 h-9"
            value={form.plazo_acreditacion_dias ?? 0}
            onChange={(e) => setForm({ ...form, plazo_acreditacion_dias: Math.max(0, Number(e.target.value) || 0) })}
            data-testid="caja-plazo-dias"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-[12px] h-9 cursor-pointer">
          <input
            type="checkbox" className="accent-[#6B1F24]"
            checked={!!form.plazo_habiles}
            onChange={(e) => setForm({ ...form, plazo_habiles: e.target.checked })}
            data-testid="caja-plazo-habiles"
          />
          Días hábiles
        </label>
      </div>
      <div className="text-[11px] text-neutral-500">0 = inmediato. Ej.: Mercado Pago 14 días corridos, tarjetas 2 días hábiles.</div>

      <div className="pt-1 border-t border-[#E5E5E5]">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest font-mono text-[#666] flex items-center gap-1.5">
            <Percent size={12} weight="bold" /> Deducciones sobre cada cobro
            {totalPct > 0 && <span className="normal-case tracking-normal text-[#6B1F24]">· total {totalPct.toLocaleString("es-AR")}%</span>}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addDed} className="rounded-sm h-7 text-[11px]" data-testid="caja-ded-add">
            <Plus size={12} className="mr-1" /> Agregar
          </Button>
        </div>
        {deds.length === 0 && <div className="text-[11px] text-neutral-400 mt-2">Sin deducciones. Agregá comisiones o impuestos (% sobre el bruto) y se generan solos al cobrar.</div>}
        <div className="space-y-2 mt-2">
          {deds.map((d, i) => (
            <div key={i} className="grid grid-cols-[1fr_84px_1fr_28px] gap-2 items-center" data-testid={`caja-ded-${i}`}>
              <Input placeholder="Nombre (ej. Comisión MP)" value={d.nombre} onChange={(e) => setDed(i, { nombre: e.target.value })} className="rounded-sm h-8 text-[12px]" data-testid={`caja-ded-nombre-${i}`} />
              <div className="relative">
                <NumberInput step="0.01" min={0} placeholder="%" value={d.porcentaje} onChange={(e) => setDed(i, { porcentaje: e.target.value })} className="rounded-sm h-8 text-[12px] pr-5" data-testid={`caja-ded-pct-${i}`} />
                <span className="absolute right-2 top-1.5 text-[11px] text-neutral-400">%</span>
              </div>
              <CuentaCombobox
                value={d.cuenta_codigo || ""}
                onChange={(codigo) => {
                  const c = (cuentas || []).find((x) => x.codigo === codigo);
                  setDed(i, { cuenta_codigo: codigo, cuenta_nombre: c?.nombre || "" });
                }}
                cuentas={cuentas}
                placeholder="Cuenta de gasto…"
                compact
                testid={`caja-ded-cuenta-${i}`}
              />
              <button type="button" onClick={() => delDed(i)} className="p-1.5 rounded-sm text-[#999] hover:text-red-600 hover:bg-red-50" data-testid={`caja-ded-del-${i}`}>
                <Trash size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function normalizarAcreditacion(form) {
  return {
    ...form,
    plazo_acreditacion_dias: Number(form.plazo_acreditacion_dias) || 0,
    plazo_habiles: !!form.plazo_habiles,
    deducciones: (form.deducciones || [])
      .filter((d) => d.nombre?.trim() && Number(d.porcentaje) > 0)
      .map((d) => ({ nombre: d.nombre.trim(), porcentaje: Number(d.porcentaje), cuenta_codigo: d.cuenta_codigo || null, cuenta_nombre: d.cuenta_nombre || null })),
  };
}
